import { useParams } from 'next/navigation';
import { memo, useEffect, useRef, useState } from 'react';

import type { TSuggestedText } from '~entities/suggestedText';
import { RESPONSE_TIMEOUT_MS } from '~entities/suggestedText';
import { events } from '~features/annals';
import { useSendDynamicEvent } from '~features/annals/useSendEvent';
import { useCurrentBot } from '~hooks/useCurrentBot';
import { useSession } from '~hooks/useSession';
import { useTranslations } from '~i18n/hooks';
import { dialogsApi } from '~services/api/dialogs';
import { Loader } from '~shared/ui/atoms';
import { buildMessageFromString } from '~utils/messages';

import { SuggestedText } from '../SuggestedText';
import {
	StyledSuggestedTextList,
	StyledSuggestedTextListContainer,
} from './styles';
export const SuggestedTextList = memo(
	() => {
		const { userId, isDemoUser } = useSession();
		const { recipientId } = useParams<{ recipientId: string }>();
		const t = useTranslations('Chats');

		const defaultOption: TSuggestedText = {
			text: t('suggestedTextContinueMessage'),
			type: 'continue',
		};

		const defaultErrorOption: TSuggestedText = {
			text: t('suggestedTextSendPhotoMessage'),
			type: 'photo',
		};

		const [sendMessage, sendMessageState] = dialogsApi.useSendMessageMutation();

		const sendDynamicEvent = useSendDynamicEvent();
		const { botLastMessage, bot } = useCurrentBot({ recipientId });
		const [fakeLoading, setFakeLoading] = useState(false);
		const timerRef = useRef<ReturnType<typeof setTimeout>>(null);
		const {
			data = { suggestions: [], messageId: 0 },
			isError,
			isLoading,
			isSuccess,
		} = dialogsApi.useGetBotSuggestedTextQuery(
			{ recipientId, userId },
			{ skip: isDemoUser }
		);

		const isUserLastMessage = botLastMessage?.senderId === userId;

		useEffect(() => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
			setFakeLoading(false);
		}, [botLastMessage?.id]);

		useEffect(() => {
			if (isError || (isSuccess && data.messageId !== botLastMessage?.id)) {
				if (!isUserLastMessage) {
					setFakeLoading(true);
					timerRef.current = setTimeout(() => {
						setFakeLoading(false);
					}, RESPONSE_TIMEOUT_MS);
				}
			} else {
				setFakeLoading(false);
			}
		}, [
			botLastMessage,
			isUserLastMessage,
			isError,
			isSuccess,
			isLoading,
			data.messageId,
		]);

		useEffect(() => {
			if (data.messageId === botLastMessage?.id) {
				setFakeLoading(false);
				if (timerRef.current) {
					clearTimeout(timerRef.current);
				}
			}
		}, [botLastMessage?.id, data]);

		const suggestionLoading = isLoading || isUserLastMessage || fakeLoading;

		const suggestions = [
			defaultOption,
			...(data.messageId === botLastMessage?.id
				? data.suggestions
				: suggestionLoading
				? []
				: [defaultErrorOption]),
		];

		if (isDemoUser) {
			return null;
		}

		return (
			<StyledSuggestedTextListContainer>
				<StyledSuggestedTextList>
					{suggestions.map((item, index) => (
						<SuggestedText
							key={item.text}
							send={() => {
								void sendMessage({
									recipientId,
									id: userId,
									message: buildMessageFromString(item.text, userId, {
										meta: {
											suggestionType: item.type,
										},
									}),
								});
								sendDynamicEvent(
									item.type === 'default'
										? events.chat.clickGeneratedMessageInChatAi
										: item.type === 'photo'
										? events.chat.clickSendphotoInChatAi
										: events.chat.clickContinueInChatAi,
									{
										bot_UserId: bot?.clientId,
										bot_name: bot?.name,
										index,
									}
								);
							}}
							text={item.text}
							type={item.type}
							// Очень короткая блокировка, мб стоит подумать над дебаунсом 1s или бизнеслогикой с ожиданием ответа бота
							disabled={sendMessageState.isLoading || fakeLoading}
						/>
					))}
					{suggestionLoading && <Loader size={32} />}
				</StyledSuggestedTextList>
			</StyledSuggestedTextListContainer>
		);
	},
	() => true
);

SuggestedTextList.displayName = 'SuggestedTextList';
